import { format } from 'date-fns';
import { importWithReload } from 'c-lib';

// Downloads from the custom dashboard: a widget's data as CSV, a widget or the whole dashboard
// as an image or PDF.

export type CsvCell = string | number | null | undefined;

const csvCell = (value: CsvCell): string => {
    if (value == null) return '';
    const text = String(value);
    return /[",\r\n]/.test(text) || /^\s|\s$/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// RFC 4180, CRLF line endings. A byte-order mark goes first so Excel reads it as UTF-8 rather
// than mangling a "£" or an accented name.
export const toCsv = (rows: CsvCell[][]): string =>
    `\uFEFF${rows.map(row => row.map(csvCell).join(',')).join('\r\n')}\r\n`;

// "Top 5 Media Owners (All Impacts)" → "top-5-media-owners-all-impacts-2026-10-30".
export const exportFileName = (title: string, now: Date = new Date()): string => {
    const slug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80);
    return `${slug || 'dashboard'}-${format(now, 'yyyy-MM-dd')}`;
};

const triggerDownload = (href: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
};

export const downloadCsv = (rows: CsvCell[][], fileName: string): void => {
    const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' }));
    triggerDownload(url, `${fileName}.csv`);
    // Revoked on the next tick: some browsers start the download asynchronously.
    setTimeout(() => URL.revokeObjectURL(url), 0);
};

// Anything marked with this attribute (download buttons, the toolbar) is left out of a capture.
export const EXPORT_IGNORE_ATTR = 'data-dashboard-export-ignore';

export const downloadElementImage = async (
    element: HTMLElement,
    fileName: string,
    kind: 'png' | 'pdf',
): Promise<void> => {
    const { default: html2canvas } = await importWithReload(() => import('html2canvas'));
    const canvas = await html2canvas(element, {
        logging: false,
        // Sharper than the screen on a normal display, without a huge file on a retina one.
        scale: Math.max(2, window.devicePixelRatio || 1),
        backgroundColor: getComputedStyle(document.body).backgroundColor || '#ffffff',
        ignoreElements: el => el.hasAttribute?.(EXPORT_IGNORE_ATTR) ?? false,
    });
    const dataUrl = canvas.toDataURL('image/png', 1);
    if (kind === 'png') {
        triggerDownload(dataUrl, `${fileName}.png`);
        return;
    }
    const { default: chartImageToPdf } = await importWithReload(
        () => import('c-reports/Components/ReportControlsWrapper/chartPdf'),
    );
    // The PDF page is sized in CSS pixels, so a 2x canvas still prints at the size it showed.
    const scale = canvas.width / element.offsetWidth || 1;
    const pdf = chartImageToPdf(dataUrl, canvas.width / scale, canvas.height / scale);
    pdf.save(`${fileName}.pdf`);
};
